import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Table,
  DatePicker,
  Space,
  Typography,
  Row,
  Col,
  Statistic,
  Select,
  Empty,
  Tag,
  Segmented,
  Button,
} from "antd";
import { DollarOutlined, DownloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  getAgentManoReconciliationImportDetails,
  getAgentManoReconciliationImports,
} from "../../../services/fhpService";
import "./Reports.scss";

const { Title, Text } = Typography;
const { Option } = Select;

const Reports = () => {
  const [activeTab, setActiveTab] = useState("financial");
  const [legacyReports, setLegacyReports] = useState([]);
  const [legacyLoading, setLegacyLoading] = useState(false);
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(30, "days"),
    dayjs(),
  ]);
  const [reportType, setReportType] = useState("all");

  const [imports, setImports] = useState([]);
  const [selectedImportId, setSelectedImportId] = useState(null);
  const [selectedImportData, setSelectedImportData] = useState(null);
  const [loadingImports, setLoadingImports] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [monthFilter, setMonthFilter] = useState(dayjs().startOf("month"));
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    setLegacyLoading(true);
    const timer = setTimeout(() => {
      setLegacyReports([]);
      setLegacyLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [dateRange, reportType]);

  const loadImports = async () => {
    setLoadingImports(true);
    try {
      const params = {};
      if (monthFilter) {
        params.report_month = monthFilter.format("YYYY-MM");
      }
      if (statusFilter !== "all") {
        params.status = statusFilter;
      }
      const response = await getAgentManoReconciliationImports(params);
      const items = response?.data?.items || [];
      setImports(items);
      if (!selectedImportId && items.length > 0) {
        setSelectedImportId(items[0].id);
      }
      console.info("[MANO_RECON_UI] agent_list_reports", { count: items.length });
    } catch (error) {
      setImports([]);
    } finally {
      setLoadingImports(false);
    }
  };

  const loadImportDetails = async (importId) => {
    if (!importId) {
      setSelectedImportData(null);
      return;
    }
    setLoadingDetails(true);
    try {
      const params = {};
      if (statusFilter !== "all") {
        params.status = statusFilter;
      }
      const response = await getAgentManoReconciliationImportDetails(importId, params);
      setSelectedImportData(response?.data || null);
      console.info("[MANO_RECON_UI] agent_view_report", { importId });
    } catch (error) {
      setSelectedImportData(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  useEffect(() => {
    loadImports();
  }, [monthFilter, statusFilter]);

  useEffect(() => {
    if (selectedImportId) {
      loadImportDetails(selectedImportId);
    }
  }, [selectedImportId, statusFilter]);

  const summary = useMemo(() => {
    const s = selectedImportData?.summary || {};
    return {
      totalOrders: Number(s.total_orders || 0),
      totalTurnover: Number(s.total_turnover || 0),
      totalPaid: Number(s.total_paid || 0),
      totalCredit: Number(s.total_credit || 0),
      totalUsdDiff: Number(s.total_usd_diff || 0),
      totalCommission: Number(s.total_commission || 0),
    };
  }, [selectedImportData]);
  const financialSummary = useMemo(() => {
    const fs = selectedImportData?.financial_summary || {};
    const totalCommissionEur = Number(fs.total_commission_eur ?? summary.totalCommission ?? 0);
    const euroRate = Number(fs.euro_rate ?? 0);
    const totalIls = Number(fs.total_ils ?? totalCommissionEur * euroRate);
    const advancesTotal = Number(fs.advances_total ?? 0);
    const finalBalance = Number(fs.final_balance ?? totalIls - advancesTotal);
    return {
      totalCommissionEur,
      euroRate,
      totalIls,
      advancesTotal,
      finalBalance,
      financialNotes: fs.financial_notes || null,
      advances: selectedImportData?.advances || [],
    };
  }, [selectedImportData, summary.totalCommission]);

  const rows = selectedImportData?.rows || [];
  const legacyTotalIncome = legacyReports
    .filter((r) => r.amount >= 0)
    .reduce((sum, r) => sum + (r.amount || 0), 0);
  const legacyTotalExpenses = legacyReports
    .filter((r) => r.amount < 0)
    .reduce((sum, r) => sum + Math.abs(r.amount || 0), 0);
  const legacyNetProfit = legacyTotalIncome - legacyTotalExpenses;

  const legacyColumns = [
    {
      title: "תאריך",
      dataIndex: "date",
      key: "date",
      align: "right",
      render: (value) => dayjs(value).format("DD/MM/YYYY"),
    },
    { title: "סוג דוח", dataIndex: "type", key: "type", align: "right" },
    { title: "סכום", dataIndex: "amount", key: "amount", align: "right" },
    {
      title: "תיאור",
      dataIndex: "description",
      key: "description",
      align: "right",
    },
    { title: "מספר הזמנה", dataIndex: "orderId", key: "orderId", align: "right" },
  ];

  const columns = [
    { title: "Order ID", dataIndex: "order_id", key: "order_id", width: 120 },
    { title: "Total", dataIndex: "total_amount", key: "total_amount", width: 110 },
    { title: "Status", dataIndex: "status", key: "status", width: 120 },
    {
      title: "Date",
      dataIndex: "order_date",
      key: "order_date",
      width: 120,
      render: (value) => (value ? dayjs(value).format("DD/MM/YYYY") : "-"),
    },
    { title: "Employee", dataIndex: "employee_name", key: "employee_name", width: 130 },
    { title: "Paid", dataIndex: "paid_amount", key: "paid_amount", width: 110 },
    { title: "Credit", dataIndex: "credit_amount", key: "credit_amount", width: 110 },
    { title: "USD Diff", dataIndex: "usd_diff_amount", key: "usd_diff_amount", width: 110 },
    { title: "Commission", dataIndex: "commission_amount", key: "commission_amount", width: 120 },
    {
      title: "Manual Status",
      dataIndex: "manual_status",
      key: "manual_status",
      width: 130,
      render: (value) => <Tag>{value || "-"}</Tag>,
    },
    { title: "Notes", dataIndex: "notes", key: "notes", width: 220 },
  ];

  return (
    <div className="reports-page">
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Card>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Title level={2}>דוחות</Title>
              <Text type="secondary">דוחות קיימים + מודול Mano Reconciliation</Text>
              <Segmented
                value={activeTab}
                onChange={setActiveTab}
                options={[
                  { label: "דוחות כספיים", value: "financial" },
                  { label: "דוחות מנו", value: "mano" },
                ]}
              />
            </Space>
          </Card>
        </Col>

        {activeTab === "financial" ? (
          <>
            <Col xs={24}>
              <Card>
                <Space wrap size="middle">
                  <Select
                    value={reportType}
                    onChange={setReportType}
                    style={{ width: 180 }}
                  >
                    <Option value="all">כל הדוחות</Option>
                    <Option value="commission">עמלות</Option>
                    <Option value="profit">רווחים</Option>
                    <Option value="expense">הוצאות</Option>
                  </Select>
                  <DatePicker.RangePicker
                    value={dateRange}
                    onChange={setDateRange}
                    format="DD/MM/YYYY"
                  />
                  <Button icon={<DownloadOutlined />}>ייצא PDF</Button>
                  <Button icon={<DownloadOutlined />}>ייצא Excel</Button>
                </Space>
              </Card>
            </Col>
            <Col xs={24}>
              <Row gutter={[12, 12]}>
                <Col xs={24} sm={8}>
                  <Card>
                    <Statistic
                      title="סה״כ הכנסות"
                      value={legacyTotalIncome}
                      prefix={<DollarOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={8}>
                  <Card>
                    <Statistic
                      title="סה״כ הוצאות"
                      value={legacyTotalExpenses}
                      prefix={<DollarOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={8}>
                  <Card>
                    <Statistic
                      title="רווח נקי"
                      value={legacyNetProfit}
                      prefix={<DollarOutlined />}
                    />
                  </Card>
                </Col>
              </Row>
            </Col>
            <Col xs={24}>
              <Card>
                {legacyReports.length === 0 && !legacyLoading ? (
                  <Empty description="אין דוחות זמינים לתקופה שנבחרה" />
                ) : (
                  <Table
                    rowKey="id"
                    loading={legacyLoading}
                    columns={legacyColumns}
                    dataSource={legacyReports}
                    pagination={{ pageSize: 10 }}
                    style={{ direction: "rtl" }}
                  />
                )}
              </Card>
            </Col>
          </>
        ) : (
          <>
            <Col xs={24}>
              <Card>
                <Space wrap size="middle">
                  <DatePicker
                    picker="month"
                    value={monthFilter}
                    onChange={(value) => setMonthFilter(value)}
                    format="MM/YYYY"
                  />
                  <Select value={statusFilter} onChange={setStatusFilter} style={{ width: 180 }}>
                    <Option value="all">כל הסטטוסים</Option>
                    <Option value="pending_review">ממתין לבדיקה</Option>
                    <Option value="approved">תקין</Option>
                    <Option value="rejected">נדחה</Option>
                    <Option value="needs_follow_up">נדרש מעקב</Option>
                  </Select>
                  <Select
                    placeholder="בחר דוח"
                    value={selectedImportId}
                    onChange={setSelectedImportId}
                    loading={loadingImports}
                    style={{ minWidth: 280 }}
                  >
                    {imports.map((item) => (
                      <Option key={item.id} value={item.id}>
                        {item.report_name} - {dayjs(item.report_month).format("MM/YYYY")}
                      </Option>
                    ))}
                  </Select>
                </Space>
              </Card>
            </Col>

            <Col xs={24}>
              <Card>
                <Row gutter={[12, 12]}>
                  <Col xs={24} sm={12} lg={8}>
                    <Card size="small">
                      <Statistic title="סה״כ עמלה באירו" value={financialSummary.totalCommissionEur} />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} lg={8}>
                    <Card size="small">
                      <Statistic title="שער אירו" value={financialSummary.euroRate} />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} lg={8}>
                    <Card size="small">
                      <Statistic title="סה״כ בש״ח" value={financialSummary.totalIls} prefix={<DollarOutlined />} />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} lg={8}>
                    <Card size="small">
                      <Statistic title="סה״כ מקדמות" value={financialSummary.advancesTotal} prefix={<DollarOutlined />} />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} lg={8}>
                    <Card size="small">
                      <Statistic
                        title="יתרה סופית"
                        value={financialSummary.finalBalance}
                        prefix={<DollarOutlined />}
                        valueStyle={{
                          color: financialSummary.finalBalance >= 0 ? "#16a34a" : "#dc2626",
                        }}
                      />
                    </Card>
                  </Col>
                  {financialSummary.financialNotes ? (
                    <Col xs={24}>
                      <Card size="small">
                        <Text>הערה פיננסית: {financialSummary.financialNotes}</Text>
                      </Card>
                    </Col>
                  ) : null}
                  <Col xs={24}>
                    <Card size="small" title="רשימת מקדמות">
                      <Table
                        rowKey="id"
                        dataSource={financialSummary.advances}
                        pagination={false}
                        size="small"
                        locale={{ emptyText: "אין מקדמות להצגה" }}
                        columns={[
                          {
                            title: "תאריך",
                            dataIndex: "advance_date",
                            key: "advance_date",
                            render: (value) =>
                              value ? dayjs(value).format("DD/MM/YYYY") : "-",
                          },
                          {
                            title: "סכום",
                            dataIndex: "amount",
                            key: "amount",
                            render: (value) => Number(value || 0).toLocaleString("he-IL"),
                          },
                          {
                            title: "הערה",
                            dataIndex: "note",
                            key: "note",
                            render: (value) => value || "-",
                          },
                        ]}
                      />
                    </Card>
                  </Col>
                </Row>
              </Card>
            </Col>
            <Col xs={24}>
              <Row gutter={[12, 12]}>
                <Col xs={24} sm={12} lg={8}>
                  <Card>
                    <Statistic title="סה״כ הזמנות" value={summary.totalOrders} />
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                  <Card>
                    <Statistic
                      title="סה״כ מחזור"
                      value={summary.totalTurnover}
                      prefix={<DollarOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                  <Card>
                    <Statistic
                      title="סה״כ שולם"
                      value={summary.totalPaid}
                      prefix={<DollarOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                  <Card>
                    <Statistic
                      title="סה״כ זיכוי"
                      value={summary.totalCredit}
                      prefix={<DollarOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                  <Card>
                    <Statistic
                      title="סה״כ הפרש דולר"
                      value={summary.totalUsdDiff}
                      prefix={<DollarOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                  <Card>
                    <Statistic
                      title="סה״כ עמלה"
                      value={summary.totalCommission}
                      prefix={<DollarOutlined />}
                    />
                  </Card>
                </Col>
              </Row>
            </Col>
            <Col xs={24}>
              <Card>
                {rows.length === 0 && !loadingDetails ? (
                  <Empty description="אין שורות להצגה עבור הבחירה הנוכחית" />
                ) : (
                  <Table
                    rowKey="id"
                    loading={loadingDetails}
                    columns={columns}
                    dataSource={rows}
                    pagination={{ pageSize: 20 }}
                    scroll={{ x: 1500 }}
                    style={{ direction: "rtl" }}
                  />
                )}
              </Card>
            </Col>
          </>
        )}
      </Row>
    </div>
  );
};

export default Reports;
